import { useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MessageSquare, Mail, Smartphone, Loader2, Sparkles, Copy, Send as SendIcon } from "lucide-react";
import { toast } from "sonner";

const PURPOSES = [
  { v: "absence_notice", label: "Absence notice" },
  { v: "performance_update", label: "Performance update" },
  { v: "event_announcement", label: "Event announcement" },
  { v: "ptm_invitation", label: "PTM invitation" },
  { v: "fee_reminder", label: "Fee reminder" },
  { v: "exam_schedule", label: "Exam schedule" },
  { v: "behavior_concern", label: "Behavior concern" },
  { v: "appreciation", label: "Appreciation" },
  { v: "custom", label: "Custom" },
];

const TONES = ["formal", "warm", "concise", "encouraging"];
const LANGS = [
  "English",
  "Hindi",
  "Kannada",
  "Tamil",
  "Telugu",
  "Malayalam",
  "Marathi",
  "Bengali",
  "Arabic",
  "French",
  "Spanish",
];

export default function ParentComms() {
  const [form, setForm] = useState({
    purpose: "absence_notice",
    student_name: "",
    grade: "",
    subject: "",
    context: "",
    tone: "warm",
    language: "English",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.context.trim()) {
      toast.error("Please describe what to convey");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/parent-comms/generate", form);
      setResult(data);
      toast.success("Messages ready");
    } catch {
      toast.error("Generation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout title="Parent Communication Assistant">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-2 tb-card p-6 space-y-4" data-testid="pc-form">
          <div>
            <Label>Purpose</Label>
            <Select value={form.purpose} onValueChange={(v) => update("purpose", v)}>
              <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="pc-purpose">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PURPOSES.map((p) => (
                  <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Student name</Label>
              <Input
                value={form.student_name}
                onChange={(e) => update("student_name", e.target.value)}
                placeholder="Aanya"
                className="mt-1.5 h-11 rounded-xl"
                data-testid="pc-student"
              />
            </div>
            <div>
              <Label>Grade / Class</Label>
              <Input
                value={form.grade}
                onChange={(e) => update("grade", e.target.value)}
                placeholder="9-A"
                className="mt-1.5 h-11 rounded-xl"
              />
            </div>
          </div>

          <div>
            <Label>Subject</Label>
            <Input
              value={form.subject}
              onChange={(e) => update("subject", e.target.value)}
              placeholder="Mathematics"
              className="mt-1.5 h-11 rounded-xl"
            />
          </div>

          <div>
            <Label>What do you want to convey?</Label>
            <Textarea
              rows={5}
              value={form.context}
              onChange={(e) => update("context", e.target.value)}
              placeholder="Aanya scored 92% in the unit test. She's been excellent in problem-solving."
              className="mt-1.5 rounded-xl"
              data-testid="pc-context"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tone</Label>
              <Select value={form.tone} onValueChange={(v) => update("tone", v)}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TONES.map((t) => (
                    <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Language</Label>
              <Select value={form.language} onValueChange={(v) => update("language", v)}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LANGS.map((l) => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button onClick={submit} disabled={busy} className="w-full h-11 rounded-full" data-testid="pc-generate">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1.5" /> Generate messages</>}
          </Button>
        </div>

        <div className="lg:col-span-3 tb-card p-6 min-h-[480px]" data-testid="pc-output">
          {!result && !busy && (
            <div className="h-full grid place-items-center text-center text-muted-foreground py-20">
              <div>
                <MessageSquare className="h-8 w-8 mx-auto opacity-50" />
                <p className="mt-3 font-display">Multilingual parent messages will appear here.</p>
                <p className="text-xs mt-1">WhatsApp · Email · SMS — generated together.</p>
              </div>
            </div>
          )}
          {busy && (
            <div className="h-full grid place-items-center text-center text-muted-foreground py-20">
              <div>
                <Loader2 className="h-8 w-8 animate-spin mx-auto" />
                <p className="mt-3 font-display">Drafting messages…</p>
              </div>
            </div>
          )}
          {result && (
            <Tabs defaultValue="whatsapp">
              <TabsList className="rounded-full bg-muted p-1">
                <TabsTrigger value="whatsapp" className="rounded-full px-4" data-testid="pc-tab-wa">
                  <MessageSquare className="h-3.5 w-3.5 mr-1.5" /> WhatsApp
                </TabsTrigger>
                <TabsTrigger value="email" className="rounded-full px-4" data-testid="pc-tab-email">
                  <Mail className="h-3.5 w-3.5 mr-1.5" /> Email
                </TabsTrigger>
                <TabsTrigger value="sms" className="rounded-full px-4" data-testid="pc-tab-sms">
                  <Smartphone className="h-3.5 w-3.5 mr-1.5" /> SMS
                </TabsTrigger>
              </TabsList>

              {result.variants.map((v) => (
                <TabsContent key={v.channel} value={v.channel} className="mt-4">
                  <Variant variant={v} language={result.language} messageId={result.message_id} />
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>
      </div>
    </Layout>
  );
}

function Variant({ variant, language, messageId }) {
  const [phone, setPhone] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [busy, setBusy] = useState(false);

  const copy = async () => {
    const text = variant.subject ? `Subject: ${variant.subject}\n\n${variant.body}` : variant.body;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const send = async () => {
    setBusy(true);
    try {
      const to = variant.channel === "email" ? emailTo : phone;
      const { data } = await api.post("/comms/send", {
        channel: variant.channel,
        to: to || null,
        subject: variant.subject || null,
        body: variant.body,
        parent_message_id: messageId,
      });
      if (data.deeplink) {
        // open the deep link in a new tab so user can finish sending
        window.open(data.deeplink, "_blank", "noopener");
        toast.success(`Opened ${variant.channel} — finish sending in the app`);
      } else if (data.delivered) {
        toast.success(`Sent via ${data.provider}`);
      } else {
        toast.warning("Saved to log — provider unavailable");
      }
    } catch {
      toast.error("Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={`pc-variant-${variant.channel}`}>
      {variant.subject && (
        <div className="rounded-2xl border border-border bg-muted/40 p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Subject</p>
          <p className="mt-1 text-sm font-medium">{variant.subject}</p>
        </div>
      )}
      <div className="rounded-2xl border border-border bg-card p-5">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">{language}</p>
        <p className="mt-2 text-sm leading-relaxed whitespace-pre-wrap" dir="auto">{variant.body}</p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-4 space-y-2">
        {variant.channel === "email" ? (
          <Input
            placeholder="parent@example.com"
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
            className="h-10 rounded-full"
            data-testid={`pc-to-${variant.channel}`}
          />
        ) : (
          <Input
            placeholder="+91XXXXXXXXXX (optional for deep link)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="h-10 rounded-full"
            data-testid={`pc-to-${variant.channel}`}
          />
        )}
        <div className="flex flex-wrap gap-2">
          <Button onClick={send} disabled={busy} size="sm" className="rounded-full" data-testid={`pc-send-${variant.channel}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <SendIcon className="h-3.5 w-3.5 mr-1.5" />}
            Send
          </Button>
          <Button onClick={copy} size="sm" variant="outline" className="rounded-full" data-testid={`pc-copy-${variant.channel}`}>
            <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
          </Button>
        </div>
      </div>
    </div>
  );
}
